import React, { createContext, useState, useEffect, useContext } from 'react';
import supabase from '../utils/supabaseClient';

// Create Auth Context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Initialize auth state
  useEffect(() => {
    initializeAuth();
    
    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user || null);
        setLoading(false);
      }
    );
    
    // Clean up the subscription
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);
  
  // Initialize auth
  const initializeAuth = async () => {
    try {
      setLoading(true);
      
      // Get current session
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      setSession(currentSession);
      setUser(currentSession?.user || null);
      
      // If user is logged in, fetch profile
      if (currentSession?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', currentSession.user.id)
          .single();
        
        if (profile && !profileError) {
          setUser(prev => ({
            ...prev,
            profile,
          }));
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Sign up
  const signUp = async (email, password, fullName, company = '', jobTitle = '') => {
    try {
      setLoading(true);
      setError(null);
      
      // Create user in Supabase Auth with profile data in metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            company: company,
            job_title: jobTitle
          }
        }
      });
      
      if (authError) throw authError;
      
      // Return the user and confirmation status
      return {
        user: authData.user,
        session: authData.session,
        confirmationRequired: !authData.session // If there's no session, confirmation is required
      };
    } catch (error) {
      console.error('Error signing up:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Sign in
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      console.error('Error signing in:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;
      
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Error signing out:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Reset password
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error resetting password:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Update profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) throw new Error('You must be logged in to update your profile');
      
      // Update user data in auth if needed
      if (profileData.displayName) {
        const { error: updateError } = await supabase.auth.updateUser({
          data: { 
            name: profileData.displayName
          }
        });
        
        if (updateError) throw updateError;
      }
      
      // Update profile in database
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          name: profileData.displayName || user.profile?.name,
          updated_at: new Date(),
        })
        .eq('user_id', user.id);
      
      if (profileError) throw profileError;
      
      // Refresh user data
      await initializeAuth();
    } catch (error) {
      console.error('Error updating profile:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Update password
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);
      
      // First verify current password by signing in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      
      if (signInError) throw new Error('Current password is incorrect');
      
      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (updateError) throw updateError;
    } catch (error) {
      console.error('Error updating password:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Clear error
  const clearError = () => {
    setError(null);
  };
  
  // Provide value to context
  const value = {
    user,
    session,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    updatePassword,
    clearError,
    isAuthenticated: !!user,
    hasActiveSubscription: !!user?.profile?.subscription_status === 'active',
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}; 